import { SxProps, Theme } from '@mui/material';
import type { CSSProperties } from 'react';
import { Color } from 'shared/utils/colors';

export const issueFormStyle: CSSProperties = {
  paddingTop: 10,
};

export const ISSUE_SIDEBAR_WIDTH_NO_APPROVAL = 248;

export const ISSUE_SIDEBAR_WIDTH_WITH_APPROVAL = 334;

export const ISSUE_SIDEBAR_MAX_WIDTH = ISSUE_SIDEBAR_WIDTH_WITH_APPROVAL;

export const ISSUE_SIDEBAR_DOCK_TRANSITION =
  'width 225ms cubic-bezier(0, 0, 0.2, 1)';

export const MOBILE_SIDEBAR_WIDTH = 'min(92vw, 420px)';

export function getWorkflowDockWidthPx(workflowHasApproval: boolean): number {
  return workflowHasApproval
    ? ISSUE_SIDEBAR_WIDTH_WITH_APPROVAL
    : ISSUE_SIDEBAR_WIDTH_NO_APPROVAL;
}

export const workflowPanelDockSx = (
  sidebarOpen: boolean,
  tabWidth: number,
  workflowHasApproval: boolean
): SxProps<Theme> => {
  const dockPx = getWorkflowDockWidthPx(workflowHasApproval);

  return {
    position: 'relative',
    flexShrink: 0,
    alignSelf: 'stretch',
    width: sidebarOpen ? `${dockPx}px` : `${tabWidth}px`,
    maxWidth: sidebarOpen ? ISSUE_SIDEBAR_MAX_WIDTH : undefined,
    transition: ISSUE_SIDEBAR_DOCK_TRANSITION,
    overflow: 'hidden',
  };
};

export const issueViewWrapperSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  width: '100%',
  minWidth: 0,
  backgroundColor: Color.solidGray,
  overflow: 'hidden',
  height: 'calc(100vh - 64px)',
  '@media (max-width: 1024px)': {
    backgroundColor: Color.white,
    height: 'calc(100dvh - 64px)',
  },
};

export const issueViewBodySx: SxProps<Theme> = {
  display: 'flex',
  flex: 1,
  minHeight: 0,
  minWidth: 0,
  alignItems: 'stretch',
  overflow: 'hidden',
  position: 'relative',
};

export const drawerSx = (
  sidebarOpen: boolean,
  isMobile: boolean,
  workflowHasApproval = false
): SxProps<Theme> => {
  const dockPx = getWorkflowDockWidthPx(workflowHasApproval);

  return {
    ...(isMobile
      ? {}
      : {
          width: '100%',
          height: '100%',
          flexShrink: 0,
          overflow: 'hidden',
        }),
    '& .MuiDrawer-paper': {
      width: isMobile ? MOBILE_SIDEBAR_WIDTH : `${dockPx}px`,
      minWidth: isMobile ? undefined : ISSUE_SIDEBAR_WIDTH_NO_APPROVAL,
      maxWidth: isMobile ? MOBILE_SIDEBAR_WIDTH : ISSUE_SIDEBAR_MAX_WIDTH,
      ...(!isMobile && !sidebarOpen ? { pointerEvents: 'none' as const } : {}),
      boxSizing: 'border-box',
      backgroundColor: Color.solidGray,
      border: 'none',
      borderRadius: isMobile ? 0 : '12px',
      margin: 0,
      ...(isMobile
        ? {
            height: '100dvh',
            overflowY: 'auto',
          }
        : {
            position: 'absolute',
            left: 0,
            top: 0,
            height: '100%',
            overflowY: 'auto',
            zIndex: 0,
          }),
    },
  };
};

export const workflowSidebarBodySx: SxProps<Theme> = {
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  width: '100%',
  minWidth: 0,
};

export const mainContentSx = (isMobile: boolean): SxProps<Theme> => ({
  flex: 1,
  minWidth: 0,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  backgroundColor: Color.white,
  overflow: 'hidden',
  borderRadius: isMobile ? 0 : '12px 0 0 0',
  margin: isMobile ? 0 : '16px 0 0 0',
  filter: isMobile
    ? 'none'
    : 'drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.05)) drop-shadow(0px 4px 12px rgba(0, 0, 0, 0.08))',
});

export const sidebarContentSx = (sidebarOpen: boolean): SxProps<Theme> => ({
  opacity: sidebarOpen ? 1 : 0,
  transform: sidebarOpen ? 'translateX(0)' : 'translateX(12px)',
  transition: sidebarOpen
    ? 'opacity 220ms 80ms ease-out, transform 220ms 80ms ease-out'
    : 'opacity 225ms cubic-bezier(0, 0, 0.2, 1), transform 225ms cubic-bezier(0, 0, 0.2, 1)',
});

export const sidebarHeaderSx: SxProps<Theme> = {
  pt: '32px',
  pl: 2,
  pr: '17px',
  pb: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
};

export const sidebarToggleButtonSx: SxProps<Theme> = {
  width: 30,
  height: 30,
  borderRadius: '50%',
  backgroundColor: Color.scrimBlack07,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  cursor: 'pointer',
  transition: 'background-color 0.15s',
  '&:hover': { backgroundColor: Color.hoverBackground },
};

export const getMainScrollableSx = (): SxProps<Theme> => ({
  overflowY: 'auto',
  overflowX: 'hidden',
  flex: 1,
  minHeight: 0,
});

export const dividerSx: SxProps<Theme> = {
  '&::before': { width: '5%' },
  '& .MuiDivider-wrapper': { paddingLeft: 0, paddingRight: '12px' },
};
